import React, { useState } from 'react';
import AnimatedSection from '../../components/AnimatedSection';
import './PortfolioPage.css';

interface PortfolioItem {
    id: number;
    title: string;
    category: string;
    image: string;
    description: string;
}

const PortfolioPage: React.FC = () => {
    const [activeFilter, setActiveFilter] = useState('all');
    const [selectedItem, setSelectedItem] = useState<PortfolioItem | null>(null);

    const categories = [
        { id: 'all', label: 'All Projects' },
        { id: 'wedding', label: 'Wedding' },
        { id: 'portrait', label: 'Portrait' },
        { id: 'event', label: 'Event' },
        { id: 'commercial', label: 'Commercial' },
        { id: 'fashion', label: 'Fashion' },
    ];

    const portfolioItems: PortfolioItem[] = [
        {
            id: 1,
            title: "Romantic Garden Wedding",
            category: "wedding",
            image: "/home_1.jpg",
            description: "A beautiful garden wedding ceremony captured in golden hour light"
        },
        {
            id: 2,
            title: "Corporate Executive Portrait",
            category: "portrait",
            image: "/home_1.jpg",
            description: "Professional headshots for business executives"
        },
        {
            id: 3,
            title: "Tech Conference 2024",
            category: "event",
            image: "/home_1.jpg",
            description: "Full coverage of major technology conference"
        },
        {
            id: 4,
            title: "Beachside Wedding Ceremony",
            category: "wedding",
            image: "/home_1.jpg",
            description: "Sunset wedding on the beautiful coast of Vietnam"
        },
        {
            id: 5,
            title: "Fashion Editorial Shoot",
            category: "fashion",
            image: "/home_1.jpg",
            description: "High-fashion editorial for luxury magazine"
        },
        {
            id: 6,
            title: "Product Photography",
            category: "commercial",
            image: "/home_1.jpg",
            description: "E-commerce product photography for premium brands"
        },
        {
            id: 7,
            title: "Family Portrait Session",
            category: "portrait",
            image: "/home_1.jpg",
            description: "Warm and natural family portraits in outdoor setting"
        },
        {
            id: 8,
            title: "Annual Gala Event",
            category: "event",
            image: "/home_1.jpg",
            description: "Elegant corporate gala with VIP guests"
        },
        {
            id: 9,
            title: "Streetwear Campaign",
            category: "fashion",
            image: "/home_1.jpg",
            description: "Urban fashion campaign for streetwear brand"
        },
        {
            id: 10,
            title: "Restaurant Branding",
            category: "commercial",
            image: "/home_1.jpg",
            description: "Food and interior photography for restaurant branding"
        },
        {
            id: 11,
            title: "Traditional Wedding",
            category: "wedding",
            image: "/home_1.jpg",
            description: "Beautiful Vietnamese traditional wedding ceremony"
        },
        {
            id: 12,
            title: "Creative Portrait Series",
            category: "portrait",
            image: "/home_1.jpg",
            description: "Artistic portrait series with creative lighting"
        },
    ];

    const filteredItems = activeFilter === 'all'
        ? portfolioItems
        : portfolioItems.filter(item => item.category === activeFilter);

    const openLightbox = (item: PortfolioItem) => {
        setSelectedItem(item);
        document.body.style.overflow = 'hidden';
    };

    const closeLightbox = () => {
        setSelectedItem(null);
        document.body.style.overflow = 'unset';
    };

    return (
        <div className="portfolio-page">
            {/* Hero Section */}
            <AnimatedSection animation="scale-up" duration="slow">
                <section className="portfolio-hero">
                    <div className="portfolio-hero-overlay">
                        <h1 className="portfolio-hero-title">Our Portfolio</h1>
                        <p className="portfolio-hero-subtitle">Explore our collection of captured moments</p>
                    </div>
                    <img src="/home_1.jpg" alt="Portfolio Hero" className="portfolio-hero-image" />
                </section>
            </AnimatedSection>

            {/* Filter Section */}
            <section className="portfolio-filter-section">
                <div className="portfolio-container">
                    <AnimatedSection animation="fade-up">
                        <div className="filter-buttons">
                            {categories.map((cat) => (
                                <button
                                    key={cat.id}
                                    className={`filter-btn ${activeFilter === cat.id ? 'active' : ''}`}
                                    onClick={() => setActiveFilter(cat.id)}
                                >
                                    {cat.label}
                                </button>
                            ))}
                        </div>
                    </AnimatedSection>
                </div>
            </section>

            {/* Gallery Section */}
            <section className="portfolio-gallery-section">
                <div className="portfolio-container">
                    <div className="portfolio-grid">
                        {filteredItems.map((item, index) => (
                            <AnimatedSection
                                key={item.id}
                                animation="scale-up"
                                delay={((index % 4) * 100) as 100 | 200 | 300 | 400}
                                className="portfolio-item"
                            >
                                <div
                                    className="portfolio-card"
                                    onClick={() => openLightbox(item)}
                                >
                                    <div className="portfolio-image-wrapper">
                                        <img src={item.image} alt={item.title} className="portfolio-image" />
                                        <div className="portfolio-overlay">
                                            <div className="portfolio-overlay-content">
                                                <span className="portfolio-category">{item.category}</span>
                                                <h3 className="portfolio-title">{item.title}</h3>
                                                <p className="portfolio-description">{item.description}</p>
                                                <span className="view-project">View Project →</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </AnimatedSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="portfolio-stats-section">
                <div className="portfolio-container">
                    <AnimatedSection animation="fade-up">
                        <h2 className="stats-section-title">Our Achievements</h2>
                    </AnimatedSection>
                    <div className="portfolio-stats-grid">
                        <AnimatedSection animation="bounce-in" delay={100} className="portfolio-stat">
                            <div className="stat-icon">📸</div>
                            <div className="stat-value">500+</div>
                            <div className="stat-label">Projects Completed</div>
                        </AnimatedSection>
                        <AnimatedSection animation="bounce-in" delay={200} className="portfolio-stat">
                            <div className="stat-icon">💒</div>
                            <div className="stat-value">200+</div>
                            <div className="stat-label">Weddings Captured</div>
                        </AnimatedSection>
                        <AnimatedSection animation="bounce-in" delay={300} className="portfolio-stat">
                            <div className="stat-icon">🏆</div>
                            <div className="stat-value">15+</div>
                            <div className="stat-label">Awards Won</div>
                        </AnimatedSection>
                        <AnimatedSection animation="bounce-in" delay={400} className="portfolio-stat">
                            <div className="stat-icon">⭐</div>
                            <div className="stat-value">100%</div>
                            <div className="stat-label">Client Satisfaction</div>
                        </AnimatedSection>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <AnimatedSection animation="fade-up">
                <section className="portfolio-cta-section">
                    <div className="portfolio-container">
                        <AnimatedSection animation="bounce-in">
                            <h2 className="cta-title">Ready to Create Your Story?</h2>
                        </AnimatedSection>
                        <AnimatedSection animation="fade-up" delay={200}>
                            <p className="cta-description">
                                Let's work together to capture your special moments and create stunning visual stories.
                            </p>
                        </AnimatedSection>
                        <AnimatedSection animation="scale-up" delay={400}>
                            <button className="cta-btn">Get in Touch</button>
                        </AnimatedSection>
                    </div>
                </section>
            </AnimatedSection>

            {/* Lightbox Modal */}
            {selectedItem && (
                <div className="lightbox-overlay" onClick={closeLightbox}>
                    <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
                        <button className="lightbox-close" onClick={closeLightbox}>×</button>
                        <img src={selectedItem.image} alt={selectedItem.title} className="lightbox-image" />
                        <div className="lightbox-info">
                            <span className="lightbox-category">{selectedItem.category}</span>
                            <h3 className="lightbox-title">{selectedItem.title}</h3>
                            <p className="lightbox-description">{selectedItem.description}</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PortfolioPage;
